"use client"

import { useMemo } from "react"
import { NoteTemplate } from "@/schema/note-template"
import { ComboboxInput } from "@ui/forms/inputs/combobox-input"
import { Button, TooltipProvider } from "@ui/shad"
import { FileText } from "lucide-react"

import { useFetchCompanyNoteTemplates } from "@/hooks/api/company-note-templates"

interface NoteTemplatePickerProps {
  onApply: (template: NoteTemplate) => void
}

export function NoteTemplatePicker({ onApply }: NoteTemplatePickerProps) {
  const { data, isLoading } = useFetchCompanyNoteTemplates()

  const noteTemplates = useMemo(() => data?.note_templates ?? [], [data?.note_templates])

  const options = useMemo(() => {
    return noteTemplates.map((template) => ({
      value: template.id,
      label: template.name,
    }))
  }, [noteTemplates])

  const handleSelect = (value: string) => {
    const template = noteTemplates.find((t) => t.id === value)
    if (template) {
      onApply(template)
    }
  }

  const triggerButton = (
    <Button size="iconXs" variant="ghost" type="button" aria-label="Apply note template" tooltip="Apply note template">
      <FileText className="text-icon h-4 w-4" />
    </Button>
  )

  if (noteTemplates.length === 0 && !isLoading) {
    return null
  }

  return (
    <TooltipProvider>
      <ComboboxInput
        id="note-template-picker"
        value=""
        onChange={handleSelect}
        options={options}
        placeholder="Search templates..."
        emptyText="No templates found"
        triggerButton={triggerButton}
        contentProps={{ align: "start", side: "top" }}
        renderOption={(option) => <span className="truncate">{option.label}</span>}
      />
    </TooltipProvider>
  )
}
